import { useState } from 'react';

interface SuperSeteGridProps {
  onSelectionChange: (selection: number[][]) => void;
  selection?: number[][];
}

export default function SuperSeteGrid({ onSelectionChange, selection }: SuperSeteGridProps) {
  const [selectedNumbers, setSelectedNumbers] = useState<number[][]>(
    Array(7).fill(null).map(() => [])
  );
  const [showInstructions, setShowInstructions] = useState(false);
  if (selection && selection.length === 7) {
    const ok = selection.every(col => Array.isArray(col));
    if (ok) {
      const same = JSON.stringify(selection) === JSON.stringify(selectedNumbers);
      if (!same) {
        setSelectedNumbers(selection.map(col => [...col].sort((a,b)=>a-b)));
      }
    }
  }

  const toggleNumber = (columnIndex: number, number: number) => {
    const newSelection = [...selectedNumbers];
    const column = newSelection[columnIndex];
    
    if (column.includes(number)) {
      // Remove number if already selected
      newSelection[columnIndex] = column.filter(n => n !== number);
    } else if (column.length < 3) {
      // Add number if less than 3 selected
      newSelection[columnIndex] = [...column, number].sort((a, b) => a - b);
    }
    
    setSelectedNumbers(newSelection);
    onSelectionChange(newSelection);
  };

  const getTotalNumbers = () => {
    return selectedNumbers.reduce((total, column) => total + column.length, 0);
  };

  const isColumnValid = (columnIndex: number) => {
    return selectedNumbers[columnIndex].length >= 1;
  };

  const isAllValid = () => {
    return selectedNumbers.every(column => column.length >= 1) && getTotalNumbers() >= 7;
  };

  return (
    <div className="space-y-6">
      {/* Header with clover icon and instructions button */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
            <span className="text-green-600 text-xl">☘️</span>
          </div>
          <h2 className="text-white text-xl font-bold lowercase">super sete</h2>
        </div>
        <button
          onClick={() => setShowInstructions(true)}
          className="px-3 py-2 bg-white border border-green-600 rounded-md text-green-600 text-xs font-semibold hover:bg-green-50"
        >
          Instruções
        </button>
      </div>

      {/* Rules text */}
      <div className="text-red-500 text-sm font-medium text-center uppercase">
        MARQUE 1 NÚMERO POR COLUNA. GANHE ACERTANDO DE 3 A 7 COLUNAS.
      </div>

      {/* Columns header aligned with grid (same gap and layout) */}
      <div className="bg-green-600 rounded-lg p-3">
        <div className="grid grid-cols-7 gap-4">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="flex items-center justify-center">
              <div className="w-10 h-10 bg-white rounded flex items-center justify-center shadow">
                <span className="text-green-600 font-bold text-lg">{i + 1}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Number grid */}
      <div className="bg-white rounded-lg p-6 border-2 border-gray-200">
        <div className="grid grid-cols-7 gap-4">
          {Array.from({ length: 7 }, (_, columnIndex) => (
            <div key={columnIndex} className="space-y-2">
              {Array.from({ length: 10 }, (_, number) => (
                <div
                  key={number}
                  className={`
                    flex items-center gap-2 p-2 rounded cursor-pointer transition-all
                    ${selectedNumbers[columnIndex].includes(number) 
                      ? 'bg-green-100 border-2 border-green-500' 
                      : 'hover:bg-gray-50 border-2 border-transparent'
                    }
                    ${isColumnValid(columnIndex) ? 'border-green-300' : ''}
                  `}
                  onClick={() => toggleNumber(columnIndex, number)}
                >
                  <div className={`
                    w-6 h-6 border-2 border-gray-300 rounded flex items-center justify-center
                    ${selectedNumbers[columnIndex].includes(number) ? 'bg-green-500 border-green-500' : ''}
                  `}>
                    {selectedNumbers[columnIndex].includes(number) && (
                      <span className="text-white text-xs">✓</span>
                    )}
                  </div>
                  <span className={`text-red-500 font-bold text-lg ${selectedNumbers[columnIndex].includes(number) ? 'text-green-600' : ''}`}>
                    {number.toString().padStart(2, '0')}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Total counter */}
      <div className="bg-gray-100 rounded-lg p-4 text-center">
        <div className="text-gray-600 text-sm mb-2">Total de números selecionados:</div>
        <div className={`text-2xl font-bold ${
          getTotalNumbers() >= 7 && isAllValid() ? 'text-green-600' : 'text-gray-400'
        }`}>
          {getTotalNumbers()} números
        </div>
        {getTotalNumbers() < 7 && (
          <div className="text-red-500 text-sm mt-2">
            Selecione pelo menos 7 números (mínimo 1 por coluna)
          </div>
        )}
      </div>

      {/* Column validation status */}
      <div className="flex justify-center gap-2">
        {Array.from({ length: 7 }, (_, i) => (
          <div
            key={i}
            className={`
              w-3 h-3 rounded-full
              ${isColumnValid(i) ? 'bg-green-500' : 'bg-gray-300'}
            `}
          />
        ))}
      </div>

      {/* Instructions modal */}
      {showInstructions && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onMouseDown={() => setShowInstructions(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full mx-4 p-6" onMouseDown={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2">Instruções do Super Sete</h3>
            <p className="text-sm text-gray-700 mb-3">
              Marque <strong>1 número por coluna</strong>. Para apostas múltiplas, você pode marcar até <strong>2</strong> números por coluna (total de <strong>8 a 14</strong> números)
              ou até <strong>3</strong> por coluna (total de <strong>15 a 21</strong> números).
            </p>
            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
              <li>São 7 colunas com números de 0 a 9.</li>
              <li>Selecione no mínimo 1 número em cada coluna.</li>
              <li>O total de números selecionados aparece no contador.</li>
              <li>O sistema valida automaticamente os limites por coluna.</li>
            </ul>
            <div className="mt-6 flex justify-end gap-2">
              <button className="px-4 py-2 text-sm bg-gray-200 rounded-md" onClick={() => setShowInstructions(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
